import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { useAvailableApps } from "./api";
import type { AvailableApp } from "./api";
import { FeaturedHero } from "./components/FeaturedHero";
import { AppShelf } from "./components/AppShelf";
import { CanvaAppCard } from "./components/CanvaAppCard";
import { CompactAppCard } from "./components/CompactAppCard";

const FOR_YOU = "__for_you__";

/* ─── Search ─── */
function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative w-full max-w-sm">
      <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        type="search"
        placeholder="Search apps"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-full border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 transition-shadow focus:border-[#2794F0] focus:outline-none focus:ring-2 focus:ring-[#2794F0]/30 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
      />
    </div>
  );
}

/* ─── Horizontal category tabs ─── */
function CategoryTabs({
  categories,
  active,
  onChange,
}: {
  categories: string[];
  active: string;
  onChange: (c: string) => void;
}) {
  const tabs = [
    { key: FOR_YOU, label: "For you" },
    { key: "", label: "All apps" },
    ...categories.map((c) => ({ key: c, label: c })),
  ];
  return (
    <div className="scrollbar-none -mx-1 flex gap-2 overflow-x-auto px-1 py-0.5">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={cn(
            "shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
            active === tab.key
              ? "bg-[#292929] text-white dark:bg-gray-100 dark:text-gray-900"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function SkeletonShelf() {
  return (
    <div className="mb-9">
      <Skeleton className="mb-3 h-5 w-40" />
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-[232px] shrink-0">
            <Skeleton className="h-[172px] w-full rounded-2xl" />
            <div className="space-y-1.5 px-4 py-3">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export function AppMarketplacePage() {
  const { data: apps, isLoading, isError, refetch } = useAvailableApps();
  const installedApps = useAuthStore((s) => s.installedApps);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(FOR_YOU);

  const handleCardClick = (app: AvailableApp) => navigate(`/apps/${app.name}`);
  const isAppInstalled = (appName: string) => installedApps.includes(appName);

  const categories = useMemo(() => {
    if (!apps) return [];
    const cats = new Set<string>();
    apps.forEach((a) => a.category && cats.add(a.category));
    return Array.from(cats).sort();
  }, [apps]);

  /** Featured = highest-rated app (falls back to first). */
  const featured = useMemo(() => {
    if (!apps || apps.length === 0) return undefined;
    return [...apps].sort(
      (a, b) => (b.average_rating ?? 0) - (a.average_rating ?? 0)
    )[0];
  }, [apps]);

  /** Apps matching the free-text search (across all categories). */
  const searchResults = useMemo(() => {
    if (!apps || !search.trim()) return [];
    const q = search.toLowerCase();
    return apps.filter(
      (a) =>
        a.app_name.toLowerCase().includes(q) ||
        a.description?.toLowerCase().includes(q) ||
        a.category?.toLowerCase().includes(q)
    );
  }, [apps, search]);

  const sortByRating = (list: AvailableApp[]) =>
    [...list].sort((a, b) => (b.average_rating ?? 0) - (a.average_rating ?? 0));

  const topPicks = useMemo(() => sortByRating(apps ?? []).slice(0, 8), [apps]);
  const yourApps = useMemo(
    () => (apps ?? []).filter((a) => isAppInstalled(a.name)),
    [apps, installedApps]
  );
  const categoryApps = useMemo(
    () => (apps ?? []).filter((a) => a.category === activeCategory),
    [apps, activeCategory]
  );

  const searching = search.trim().length > 0;

  return (
    <div className="w-full px-6 py-6">
      {/* ── Header ── */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          App Store
        </h1>
        <p className="mt-1 text-[15px] text-gray-500 dark:text-gray-400">
          Find the best tools to grow your business
        </p>
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <>
          <Skeleton className="mb-8 h-40 w-full rounded-3xl" />
          <SkeletonShelf />
          <SkeletonShelf />
        </>
      )}

      {/* ── Error ── */}
      {isError && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="mb-4 rounded-full bg-red-50 p-3 dark:bg-red-900/20">
            <RefreshCw className="h-6 w-6 text-red-500" />
          </div>
          <p className="mb-1 font-medium text-gray-600 dark:text-gray-400">
            Couldn&apos;t load apps
          </p>
          <p className="mb-4 text-sm text-gray-400 dark:text-gray-500">
            Check your connection and try again.
          </p>
          <Button variant="outline" onClick={() => refetch()} className="gap-2 rounded-full">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      )}

      {/* ── Empty ── */}
      {!isLoading && !isError && apps?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No apps available yet. We&apos;re adding new tools soon.
          </p>
        </div>
      )}

      {/* ── Content ── */}
      {!isLoading && !isError && apps && apps.length > 0 && (
        <>
          {/* Hero — hidden while searching to keep results focused */}
          {featured && !searching && (
            <FeaturedHero
              app={featured}
              isInstalled={isAppInstalled(featured.name)}
              onClick={handleCardClick}
            />
          )}

          {/* Sticky search + category tabs */}
          <div className="sticky top-0 z-10 -mx-6 mb-7 space-y-3 bg-gray-50/90 px-6 py-3 backdrop-blur dark:bg-[#0f1015]/90">
            <SearchInput value={search} onChange={setSearch} />
            {!searching && (
              <CategoryTabs
                categories={categories}
                active={activeCategory}
                onChange={setActiveCategory}
              />
            )}
          </div>

          {/* Search results — flat responsive grid */}
          {searching ? (
            searchResults.length > 0 ? (
              <section className="mb-9">
                <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {searchResults.length} result{searchResults.length > 1 ? "s" : ""} for
                  &ldquo;{search}&rdquo;
                </h2>
                <div className="flex flex-wrap gap-4">
                  {searchResults.map((app) => (
                    <CanvaAppCard
                      key={app.name}
                      app={app}
                      isInstalled={isAppInstalled(app.name)}
                      onClick={handleCardClick}
                    />
                  ))}
                </div>
              </section>
            ) : (
              <div className="py-16 text-center">
                <p className="text-gray-500 dark:text-gray-400">
                  No apps match &ldquo;{search}&rdquo;
                </p>
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="mt-2 text-sm text-[#2794F0] hover:underline"
                >
                  Clear search
                </button>
              </div>
            )
          ) : activeCategory === FOR_YOU ? (
            /* ── For you: curated shelves + new & noteworthy grid ── */
            <>
              {yourApps.length > 0 && (
                <AppShelf
                  title="Your apps"
                  subtitle="Installed in this workspace"
                  apps={yourApps}
                  isInstalled={isAppInstalled}
                  onCardClick={handleCardClick}
                />
              )}
              <AppShelf
                title="Top picks for your business"
                subtitle="The highest-rated tools other businesses love"
                apps={topPicks}
                isInstalled={isAppInstalled}
                onCardClick={handleCardClick}
              />
              {categories.map((cat) => (
                <AppShelf
                  key={cat}
                  title={cat}
                  apps={(apps ?? []).filter((a) => a.category === cat)}
                  isInstalled={isAppInstalled}
                  onCardClick={handleCardClick}
                />
              ))}

              <section className="mb-4">
                <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  All apps
                </h2>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  Browse the full catalogue
                </p>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                  {sortByRating(apps).map((app) => (
                    <CompactAppCard
                      key={app.name}
                      app={app}
                      isInstalled={isAppInstalled(app.name)}
                      onClick={handleCardClick}
                    />
                  ))}
                </div>
              </section>
            </>
          ) : activeCategory === "" ? (
            /* ── All apps: full responsive grid of cards ── */
            <section className="mb-9">
              <div className="flex flex-wrap gap-4">
                {sortByRating(apps).map((app) => (
                  <CanvaAppCard
                    key={app.name}
                    app={app}
                    isInstalled={isAppInstalled(app.name)}
                    onClick={handleCardClick}
                  />
                ))}
              </div>
            </section>
          ) : (
            /* ── Single category: responsive grid of cards ── */
            <section className="mb-9">
              <div className="flex flex-wrap gap-4">
                {sortByRating(categoryApps).map((app) => (
                  <CanvaAppCard
                    key={app.name}
                    app={app}
                    isInstalled={isAppInstalled(app.name)}
                    onClick={handleCardClick}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
